/*eslint-disable*/
import bowser from "bowser";
import flow from "lodash/flow";
import fp from "lodash/fp";
import flattenDeep from "lodash/flattenDeep";
import forEach from "lodash/forEach";
import get from "lodash/get";
import has from "lodash/has";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isBoolean from "lodash/isBoolean";
import isEmpty from "lodash/isEmpty";
import isPlainObject from "lodash/isPlainObject";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import keys from "lodash/keys";
import lang from "lodash/lang";
import merge from "lodash/merge";
import noop from "lodash/noop";
import object from "lodash/object";
import set from "lodash/set";
import trim from "lodash/trim";
import values from "lodash/values";
import moment from "moment";
import React from "react";
import { render } from "react-dom";
import { Router } from "react-router";
import URI from "urijs";

import AuthStore from "./stores/chat/AuthStore";
import ChatActions from "./actions/ChatActions";
import ChatEventTypes from "./constants/ChatEventTypes";
import ChatWebAPI from "./utils/chat/ChatWebAPI";
import alt from "./alt/ChatAlt";
import chatNavigation, { history } from "./utils/chat/chatNavigation";
import Analytics from "./utils/chat/Analytics";
import ChatRoutes from "./routes/ChatRoutes";
import ChatStorage from "./utils/chat/ChatStorage";
import defaultConfig from "./utils/chat/defaultChatConfig";
import { postMessageToParent, trackEventForKnowledgeBase, validateEmail, validatePhone } from "./utils/Utils";
import firebaseMessaging from "./utils/chat/firebaseMessaging";
import { triggerReadyEvent, triggerOnErrorEvent } from "./utils/chat/EventHelper";
import { PostMessage } from "./constants/chat";
import FirebaseConstants from "./constants/FirebaseConstants";
import i18next from "./utils/i18next";
import { EVENT_CATEGORIES, EVENT_TRACKER } from "./constants/AnalyticsConstants";
import JwtHelper from "./utils/chat/JwtHelper";

let chatInstance;

export class Chat {
    constructor(config) {
        const mountNode = document.querySelector(".js-chat-container");
        const routes = ChatRoutes;
        const { userEmail, userName, userPhone } = config;

        this.config = config;
        alt.bootstrap(
            JSON.stringify({
                AuthStore: {
                    isLoading: true,
                    isLoggedIn: false,
                },
                AskFormStore: {
                    contactInfo: userEmail || userPhone || "",
                    error: null,
                    hasContactInfo:
                        (!isEmpty(userEmail) && validateEmail(userEmail)) ||
                        (!isEmpty(userPhone) && validatePhone(userPhone)),
                    isProcessing: false,
                    lastResponseTimeInMillis: null,
                    questionText: "",
                    showKnowledgeBaseArticleOverlay: false,
                    userName: userName || "",
                },
                ConfigStore: {
                    config: this.config,
                },
            })
        );
        ChatStorage.init({ chatConfigId: config.id });
        const oldToken = JwtHelper.getJwtToken();
        if (window.jwtToken) {
            const leeway = (window.companySettings.jwtAcceptLeewayInMinutes || 0) * 60;
            JwtHelper.setJwtToken(window.jwtToken, leeway);
        }

        if (oldToken && window.jwtToken === JwtHelper.getJwtToken() && ChatStorage.getItem("path")) {
            ChatStorage.setItem("path", null);
        }
        Analytics.init({ trackerName: EVENT_TRACKER.RTM, eventCategory: EVENT_CATEGORIES.RTM });
        chatNavigation.init();
        this.checkBrowserNotificationPermissions();

        render(<Router history={history} routes={routes} />, mountNode);
    }

    [PostMessage.TRACK_EVENT]([eventName, { text, userId }]) {
        if (!includes(values(ChatEventTypes), eventName)) {
            console.error(`Invalid configuration: ${eventName} is not a valid event name.`); // eslint-disable-line no-console
            return;
        }

        trackEventForKnowledgeBase({
            configId: this.config.id,
            key: text,
            type: eventName,
            userId,
        });
    }

    [PostMessage.MAXIMIZE]() {
        return noop();
    }

    [PostMessage.MINIMIZE]() {
        return noop();
    }

    [PostMessage.MAXIMIZE_ASK_QUESTION]() {
        return noop();
    }

    [PostMessage.NAVIGATE]() {
        return noop();
    }

    [PostMessage.ASK_QUESTION]([
        {
            questionText,
            email,
            name,
            questionCategory = "",
            customTags = [],
            metadata = {},
            emailPrefilled = false,
            phone,
        },
    ]) {
        const contactInfo = email || phone || this.config.userEmail || this.config.userPhone;

        if (questionText) {
            ChatActions.askQuestion({
                contactInfo,
                questionText: trim(questionText),
                userName: name || this.config.userName,
                questionCategory,
                customTags,
                metadata,
                emailPrefilled,
                configId: this.config.id,
            });
        } else {
            // TODO: figure out a way to pass errors back to caller
            throw new Error("'questionText' can't be empty.");
        }
    }

    [PostMessage.SET_CONFIG]([key, value]) {
        switch (key) {
            case "active":
            case "id":
                throw new Error(`${key} can not be changed after the widget was initilised.`);

            case "displayAskQuestion":
            case "showDisclaimer":
            case "disableAskNewQuestionFromUi":
                if (isBoolean(value)) {
                    this.config[key] = value;
                    ChatActions.updateConfig(this.config);
                } else {
                    throw new TypeError(`${key} expects a boolean.`);
                }
                break;

            case "customTags":
                if (isArray(value)) {
                    this.config[key] = flattenDeep(value);
                    ChatActions.updateConfig(this.config);
                } else {
                    throw new TypeError(`${key} expects an array.`);
                }
                break;

            case "metadata":
                if (isPlainObject(value)) {
                    this.config[key] = value;
                    ChatActions.updateConfig(this.config);
                } else {
                    throw new TypeError(`${key} expects a plain object.`);
                }
                break;

            case "questionCategory":
                if (isString(value)) {
                    this.config[key] = value;
                    ChatActions.updateConfig(this.config);
                } else {
                    throw new TypeError(`${key} expects a string.`);
                }
                break;

            case "labels":
                if (isPlainObject(value)) {
                    forEach(value, (labelText, labelKey) => {
                        const isValid = includes(keys(defaultConfig.labels), labelKey);

                        if (isValid) {
                            this.config[key][labelKey] = labelText;
                            ChatActions.updateConfig(this.config);
                        } else {
                            throw new Error(`${labelKey} is not a valid key for setting labels. skipping...`);
                        }
                    });
                } else {
                    throw new TypeError(`${key} expects a plain object.`);
                }
                break;

            case "userName":
            case "userEmail":
            case "userPhone":
                throw new Error(`Updating ${key} is not supported yet.`);

            default:
                throw new Error(`${key} is not a valid configuration property.`);
        }
    }

    [PostMessage.GET_STATE]() {
        ChatWebAPI.get("/chat/state")
            .query({ cfgId: this.config.id })
            .end((error, response) => {
                postMessageToParent(PostMessage.RETURN_GET_STATE, {
                    data: {
                        questions: get(response, "body.data.questions"),
                        session: get(response, "body.data.session", false),
                    },
                });
            });
    }

    [PostMessage.UPDATE_METADATA]([{ metadata, questionUuid }]) {
        ChatWebAPI.post("/chat/updateMetadata")
            .send({ questionMetadata: metadata, questionUuid })
            .type("json")
            .end();
    }

    [PostMessage.REROUTE]([{ questionUuid }]) {
        ChatWebAPI.post("/chat/reroute")
            .send({ chatId: questionUuid })
            .type("json")
            .end(error => {
                postMessageToParent(PostMessage.RETURN_REROUTE, {
                    data: {
                        success: !error,
                    },
                });
            });
    }

    [PostMessage.OPTOUT]([{ questionUuid }]) {
        ChatWebAPI.post(`/chat/closeFromOptOut`)
            .send({ chatId: questionUuid ,future: true })
            .type("json")
            .end(error => {
                postMessageToParent(PostMessage.RETURN_REROUTE, {
                    data: {
                        success: !error,
                    },
                });
            });
    }

    checkBrowserNotificationPermissions() {
        if (!bowser.chrome) {
            return;
        }

        firebaseMessaging
            .getToken()
            .then(token => {
                if (token) {
                    ChatActions.grantBrowserNotifications();
                }
            })
            .catch(error => {
                if (error.code === FirebaseConstants.ERROR_NOTIFICATIONS_BLOCKED) {
                    ChatActions.denyBrowserNotifications();
                }
            });
    }

    denyBrowserNotifications() {
        ChatActions.denyBrowserNotifications();
    }

    grantBrowserNotifications({ token }) {
        ChatWebAPI.get("/pushNotification/register")
            .query({ regId: token, type: "BROWSER" })
            .end(error => {
                if (!error) {
                    ChatActions.grantBrowserNotifications(token);
                }
            });
    }

    [PostMessage.LOG_OUT]() {
        const { isLoggedIn } = AuthStore.getState();

        if (isLoggedIn) {
            ChatActions.logout();
        }
    }
}

function mergeCustomizer(objectValue, sourceValue) {
    const isEmptyString = lang.isString(sourceValue) && sourceValue.length === 0;

    return isEmptyString || lang.isNil(sourceValue) ? objectValue : undefined;
}

function shouldOverrideLabel(type, label, locale, localConfig) {
    return type && !has(localConfig, `i18n.${locale}.${label}`);
}

function getConfigLabels(localConfig, serverConfig) {
    const config = object.merge(serverConfig, localConfig);
    const currentLocale = get(config, "lang", "");
    const { labels } = config;
    const locale = i18n.getLocale(window.companySettings.supportedLanguages, currentLocale);

    i18next.changeLanguage(locale);
    if (locale === "zh") {
        moment.locale(`${locale}-cn`);
    } else {
        moment.locale(locale);
    }

    const dataByLanguage = i18next.getDataByLanguage(locale);
    const dataLanguage = has(config, `i18n.${locale}`)
        ? merge({}, dataByLanguage, {
              translation: {
                  rtm: config.i18n[locale],
              },
          })
        : dataByLanguage;

    if (locale === "en" && labels) {
        if (shouldOverrideLabel(labels.askBubble, "button.ask_our_experts", locale, config)) {
            set(dataLanguage, "translation.rtm.button.ask_our_experts", labels.askBubble);
        }
        if (shouldOverrideLabel(labels.askButton, "button.send_to_our_experts", locale, config)) {
            set(dataLanguage, "translation.rtm.button.send_to_our_experts", labels.askButton);
        }
        if (shouldOverrideLabel(labels.askFormHeader, "header.how_can_we_help_you", locale, config)) {
            set(dataLanguage, "translation.rtm.header.how_can_we_help_you", labels.askFormHeader);
        }
    }

    i18next.addResourceBundle(locale, "translation", dataLanguage.translation);

    return {
        labels: {
            askBubble: i18n.t("rtm.button.ask_our_experts"),
            askButton: i18n.t("rtm.button.send_to_our_experts"),
            askFormHeader: i18n.t("rtm.header.how_can_we_help_you"),
        },
    };
}

function initChat(localConfig) {
    const config = object.mergeWith(
        {},
        defaultConfig,
        window.serverConfig,
        localConfig,
        getConfigLabels(localConfig, window.serverConfig),
        mergeCustomizer
    );
    const isActive = typeof localConfig.active === "undefined" ? window.serverConfig.active : localConfig.active;

    if (!chatInstance && isActive) {
        chatInstance = new Chat(config);
    }

    ChatWebAPI.get("/chat/state")
        .query({ cfgId: config.id })
        .end((error, response) => {
            triggerReadyEvent({ config, response });
        });
}

window.addEventListener("message", ({ data: { action, params }, origin, source }) => {
    const documentReferrer = new URI(document.referrer);
    const currentWindow = new URI(window.location);
    const isValidOrigin = flow(
        fp.split(","),
        fp.map(trim),
        fp.map(url => new URI(url).origin()),
        fp.filter(url => !isEmpty(url)),
        fp.includes(origin)
    )(window.serverConfig.validOriginList);

    if (
        (!(isValidOrigin || origin === documentReferrer.origin()) || source !== window.parent.window) &&
        origin !== currentWindow.origin()
    ) {
        return;
    }

    if (isUndefined(action)) {
        return;
    }

    if (action === PostMessage.IFRAME_LOADED) {
        initChat(params);
        return;
    }

    try {
        if (!chatInstance) {
            throw new Error(`DirectlyRTM instance not initialized or disabled.`);
        }

        if (typeof chatInstance[action] === "function") {
            chatInstance[action].call(chatInstance, params);
        } else {
            throw new Error(`Unknown action: ${action}`);
        }
    } catch (exception) {
        // console.error(`${exception.name}: ${exception.message} : 1`); // eslint-disable-line no-console

        const errorEvent = {
            name: exception && exception.name ? exception.name : "Error",
            message: exception && exception.message ? exception.message : "Unknown error",
            timestamp: new Date().toISOString(),
            errorInfo: "Chat Widet is crashed",
            action: "Ask_question",
            params: params,
        };

        triggerOnErrorEvent(errorEvent);
    }
});
